import { create } from 'zustand';
import { DEFAULT_PENS } from 'shared';
import { api } from './api.js';
import type { SyncState } from './sync/sync.js';

export interface PenPreset { color: string; size: number; label: string }

export interface Settings {
  legend: string;
  rules: string;
  vocabulary: string;
  pens: PenPreset[];
  defaultPaper: 'ruled' | 'grid' | 'dotted' | 'blank';
  fingerDraw: boolean;
  palmRejectPx: number;
  lowLatencyInk: boolean;
  autoSendHighConfidence: boolean;
  appUrl: string;
  timezone: string;
}

interface AppState {
  authenticated: boolean | null;
  settings: Settings | null;
  sync: { state: SyncState; pending: number; lastAt: number | null };
  toast: string | null;
  debug: boolean;
  setAuthenticated(v: boolean): void;
  setSettings(s: Settings): void;
  setSync(s: AppState['sync']): void;
  showToast(msg: string | null): void;
  toggleDebug(): void;
  loadSettings(): Promise<void>;
}

export const useApp = create<AppState>((set, get) => ({
  authenticated: null,
  settings: null,
  sync: { state: 'idle', pending: 0, lastAt: null },
  toast: null,
  debug: localStorage.getItem('cv.debug') === '1',

  setAuthenticated: (authenticated) => set({ authenticated }),
  setSettings: (settings) => set({ settings }),
  setSync: (sync) => set({ sync }),
  showToast: (toast) => {
    set({ toast });
    if (toast) window.setTimeout(() => { if (get().toast === toast) set({ toast: null }); }, 4000);
  },
  toggleDebug: () => set((s) => {
    localStorage.setItem('cv.debug', s.debug ? '0' : '1');
    return { debug: !s.debug };
  }),

  async loadSettings() {
    try {
      const settings = await api.get<Settings>('/api/settings');
      set({ settings, authenticated: true });
    } catch {
      // Sem rede, o app precisa abrir mesmo assim: usa o que está no
      // localStorage e segue offline (SPEC §3.2).
      const cached = localStorage.getItem('cv.settings');
      if (cached) set({ settings: JSON.parse(cached) as Settings });
    }
  },
}));

useApp.subscribe((s) => {
  if (s.settings) localStorage.setItem('cv.settings', JSON.stringify(s.settings));
});

export const defaultPens: PenPreset[] = DEFAULT_PENS;
